import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  Input,
  OnInit,
  output,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { cloneDeep } from 'lodash-es';
import { QueryOptions } from 'odata-query';
import { delay, tap } from 'rxjs';
import { EntityType } from 'src/app/modules/admin/models';
import { EntityTypeService } from 'src/app/modules/admin/services';

@Component({
  standalone: false,
  selector: 'gstar-entity-types-combobox',
  templateUrl: './entity-types-combobox.component.html',
  styleUrls: ['./entity-types-combobox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntityTypesComboboxComponent implements OnInit {
  private readonly entityTypeService = inject(EntityTypeService);

  @Input() value!: EntityType | null | undefined;
  placeholder = input<string | undefined>(undefined);
  required = input<boolean>(false);
  disabled = input<boolean>(false);
  onlyActives = input<boolean>(false);
  hasSelectedJurisdiction = input<boolean>(false);
  filterByJurisdiction = input<boolean>(true);

  onlyDefaults = input<boolean>(false);
  onlyNonDefaults = input<boolean>(false);

  selectEntityType = output<EntityType | null | undefined>();
  clearTargetEntityTypeValue = output<void>();

  entityTypes: WritableSignal<EntityType[]> = signal<EntityType[]>([]);
  listLoading = signal<boolean>(true);

  query: Partial<QueryOptions<EntityType>> = {
    filter: '',
  };
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    const filters: string[] = [];

    if (this.onlyActives()) {
      filters.push('isActive eq true');
    }

    if (this.onlyDefaults()) {
      filters.push('isDefault eq true');
    }

    if (this.onlyNonDefaults()) {
      filters.push('isDefault eq false');
    }

    this.query.filter = filters.join(' and ');

    if (this.filterByJurisdiction()) {
      this.entityTypeService.query.set(this.query);
      this.entityTypeService.cancelRefreshEntityTypes();
      this.entityTypeService.refreshEntityTypes();
    }

    const entityTypes$ = this.filterByJurisdiction()
      ? this.entityTypeService.entityTypesByJurisdiction
      : this.entityTypeService.get(this.query);

    entityTypes$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.listLoading.set(true)),
        tap(entitiesResponse => {
          if (this.hasSelectedJurisdiction()) {
            this.selectEntityTypeHandler({} as EntityType);
          }
          this.entityTypes.set(
            cloneDeep(entitiesResponse.items).filter(entityType => {
              if (this.onlyActives() && !entityType.isActive) {
                return false;
              }

              if (this.onlyDefaults() && !entityType.isDefault) {
                return false;
              }

              if (this.onlyNonDefaults() && entityType.isDefault) {
                return false;
              }

              return true;
            })
          );
        }),
        delay(0), // Due to Appkit bug, we need to add a delay to force the re-rendering of combobox
        tap(() => this.listLoading.set(false))
      )
      .subscribe();
  }

  selectEntityTypeHandler(entityType: EntityType | null | undefined): void {
    this.value = entityType;
    this.selectEntityType.emit(entityType);
  }

  clearTargetEntityType(): void {
    this.value = null;
    this.clearTargetEntityTypeValue.emit();
  }
}
